/**
 * Visitor event handlers — analytics tracking + state mutations.
 * Extracted from VisitorMapBySlug to reduce orchestration layer size.
 */

import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { POI } from "../../components/MapCanvas";
import type { EmergencyAction } from "../../components/visitor/VisitorMenu";
import type { CategoryForCarousel } from "../../components/visitor/CategoryCarousel";
import type { POIDestination } from "../useWayfinding";
import { analytics } from "../../lib/analytics";
import { toast } from "@wondermaps/ui";

interface UseVisitorHandlersParams {
  pois: POI[];
  effectiveCategory: string | null | undefined;
  carouselCategories: CategoryForCarousel[];
  happeningNowPoiIds: Set<string>;
  navigateToCategory: (slug: string, opts?: { forceListView?: boolean }) => void;
  goToMap: () => void;
  handleMapPOIClick: (poi: POI) => void;
  handleSheetClose: () => void;
  pushPOIFromSheet: (poi: POI, context: { type: "poi"; poiId: string; context: "map" | "list" | "events" | "curated" }) => void;
  isBookmarked: (poiId: string) => boolean;
  toggleBookmark: (poiId: string) => void;
  selectedPOI: POI | null;
  previewRoute: (destination: POIDestination) => boolean;
}

export function useVisitorHandlers({
  pois,
  effectiveCategory,
  carouselCategories,
  happeningNowPoiIds,
  navigateToCategory,
  goToMap,
  handleMapPOIClick,
  handleSheetClose,
  pushPOIFromSheet,
  isBookmarked,
  toggleBookmark,
  selectedPOI,
  previewRoute,
}: UseVisitorHandlersParams) {
  const { t } = useTranslation("map");

  const handleSelectCategory = useCallback((categorySlug: string, forceListView?: boolean) => {
    const isClearing = categorySlug === "all";
    const previousCategory = effectiveCategory;
    const categoryName = isClearing
      ? "All"
      : categorySlug === "saved"
        ? "Saved"
        : categorySlug === "happening-now"
          ? "Happening Now"
          : carouselCategories.find((c) => c.slug === categorySlug)?.name ?? categorySlug;

    analytics.filterCategory({
      category_id: categorySlug,
      category_name: categoryName,
      action: isClearing ? "clear_all" : previousCategory === "all" ? "add" : "remove",
    });

    if (categorySlug === "happening-now") {
      analytics.filterHappeningNow({ poi_count: happeningNowPoiIds.size });
    }

    navigateToCategory(categorySlug, { forceListView });
  }, [effectiveCategory, carouselCategories, happeningNowPoiIds.size, navigateToCategory]);

  const handleEmergencyAction = useCallback((action: EmergencyAction) => {
    analytics.emergencyAction({
      action_id: action.id,
      action_type: action.actionType,
      action_label: action.label,
    });

    if (action.actionType === "navigate") {
      const poi = pois.find((p) => p.id === action.target);
      if (poi) {
        goToMap();
        handleMapPOIClick(poi); // includes flyTo with sheet padding
      }
    } else if (action.actionType === "category") {
      goToMap();
      navigateToCategory(action.target, { forceListView: true });
    }
    // "call" is handled natively by <a href="tel:"> in MorePage
  }, [pois, goToMap, handleMapPOIClick, navigateToCategory]);

  /** Navigate from events list to POI — pushes onto stack (events stays underneath) */
  const handleEventNavigateToPoi = useCallback((poiId: string, event: { id: string; name: string; status: string }) => {
    analytics.selectEvent({
      event_id: event.id,
      event_name: event.name,
      poi_id: poiId,
      status: event.status as "happening-now" | "today-upcoming" | "upcoming",
    });

    const poi = pois.find((p) => p.id === poiId);
    if (poi) {
      pushPOIFromSheet(poi, { type: "poi", poiId: poi.id, context: "events" });
    }
  }, [pois, pushPOIFromSheet]);

  const handleBookmarkToggle = useCallback((poiId: string, source: "list" | "detail") => {
    const poi = pois.find((p) => p.id === poiId);
    const wasBookmarked = isBookmarked(poiId);
    toggleBookmark(poiId);
    if (poi) {
      if (wasBookmarked) {
        analytics.unbookmarkPoi({ poi_id: poiId, poi_name: poi.name, source });
      } else {
        analytics.bookmarkPoi({ poi_id: poiId, poi_name: poi.name, source });
      }
    }
  }, [pois, isBookmarked, toggleBookmark]);

  const handleGetDirections = useCallback(() => {
    if (!selectedPOI?.location) return;
    const poiDestination: POIDestination = {
      id: selectedPOI.id,
      name: selectedPOI.name,
      coords: [
        selectedPOI.location.coordinates[0],
        selectedPOI.location.coordinates[1],
      ],
      categoryName: selectedPOI.category?.name ?? null,
      categoryIcon: selectedPOI.category?.icon ?? null,
    };
    analytics.interactionNavigate({
      poi_id: selectedPOI.id,
      poi_name: selectedPOI.name,
      destination: `${selectedPOI.location.coordinates[1]},${selectedPOI.location.coordinates[0]}`,
    });
    const routeFound = previewRoute(poiDestination);
    if (routeFound) {
      handleSheetClose();
    } else {
      toast({ title: t("directions.noRouteAvailable"), duration: 4000 });
    }
  }, [selectedPOI, previewRoute, handleSheetClose, t]);

  return {
    handleSelectCategory,
    handleEmergencyAction,
    handleEventNavigateToPoi,
    handleBookmarkToggle,
    handleGetDirections,
  };
}
